import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MdbAccordionModule, MdbIconModule, MdbTabsModule } from '../../lib/mdb5-adapted/public-api';
import {
  BehaviorSubject, combineLatest, debounceTime, filter, map, Observable, of, startWith, Subject, Subscription,
  switchMap, takeUntil, tap,
} from 'rxjs';

import { LandingService } from 'src/app/landing/landing.service';
import { environment } from 'src/environments/environment';

import { AppUtilityService } from '../app-utility.service';
import { QuoteStoreService } from '../core/rest-services/quote-store.service';
import { UserService } from '../core/services/lmsuser.service';
import { StorageService } from '../core/services/storage.service';
import { LMSDialogHandlerService } from '../shared/lmsdialog-handler.service';
import { ZARcurrencyPipe } from '../shared/pipes/zarcurrency.pipe';
import { LmsGridviewComponent } from '../shared/ui/lms-gridview/lms-gridview.component';

interface BranchSelection {
  branchSerial: string;
  branchName?: string;
}


@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone:true,
  imports: [
    CommonModule,
    MdbTabsModule,
    ReactiveFormsModule,
    MdbIconModule,
    MdbAccordionModule,
    ZARcurrencyPipe,
    LmsGridviewComponent
]
})
export class LandingComponent implements OnInit, OnDestroy {
  @Input() selectedBranchValue = this.landingService.selectedBranch;
  @Input() enableClientList: boolean | false;

  @Input() showFetch!: boolean | false;
  @Input() withFingerCheck!: boolean | false;

  @Input() withBiometrics!: boolean | true;
  @Input() withCompliance: boolean | false;
  @Output() togglePanelOpenstate: EventEmitter<any> = new EventEmitter<any>();
  @Output() onActiveClientSelect = new EventEmitter<any>();
  @Output() onNewClientSelect = new EventEmitter<any>();
  @Output() onViewAllClients = new EventEmitter<any>();

  @Output() onbranchChanged = new EventEmitter<any | undefined>();
  @Output() onComplianceUpdate = new EventEmitter<any>();

  env = environment;
  useNewLandingLayout: boolean;
  LandingUserDisplayName: string = '';
  LandingUserLoginName: string = '';

  ClientListLoading = this.landingService.clientListLoading$;
  NewListLoading = this.landingService.newListLoading$;
  ComplinaceListLoading = this.landingService.complianceList$;

  PanelOpenState: number = 1;
  selectedCategory: 'active' | 'new' | 'compliance' | null = null;
  selectedCategory$ = new BehaviorSubject<'active' | 'new' | 'compliance' | null>(null);
  searchControl = new FormControl('');
  statusFilterControl = new FormControl('all');
  sortControl = new FormControl('dateDesc');
  viewMode: 'cards' | 'table' = 'cards';

  // Single filtered observable that switches between data sources based on selected category
  filteredQuotes$!: Observable<any[]>;

  // Loading observable that switches based on selected category
  isLoading$!: Observable<boolean>;

  // Filter options
  statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'new', label: 'New' },
    { value: 'pending', label: 'Pending' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'nupay', label: 'Nupay' }
  ];

  sortOptions = [
    { value: 'dateDesc', label: 'Date (Newest First)' },
    { value: 'dateAsc', label: 'Date (Oldest First)' },
    { value: 'applicationAsc', label: 'Application # (Asc)' },
    { value: 'applicationDesc', label: 'Application # (Desc)' },
    { value: 'nameAsc', label: 'Name (A-Z)' },
    { value: 'nameDesc', label: 'Name (Z-A)' }
  ];

  private subscriptions: Subscription[] = [];
  @Output() branchName: string = '';
  @Output() branchSerial: string = '';

  private _userbranchName: string;
  private _userbranchSerial: string;
  private _userDisplayName: string;
  public LandingProfile?: any | null = null;
  private _selectedBranch: any = {};

  tableActive_config = {
    columns: [
      {
        key: 'quoteId',
        heading: 'Application #'
      },
      {
        key: 'idNumber',
        heading: 'Id Number'
      },
      {
        key: 'firstName',
        heading: 'Name'
      },
      {
        key: 'lastName',
        heading: 'Surname'
      },
      {
        key: 'status',
        heading: 'Status',
        status: true
      },
      {
        key: 'userName',
        heading: 'User'
      }
    ],
    primary_key_set: ['quoteId'],
    table_data_changer: new BehaviorSubject<any[]>([]),
    ediTable: {
      add: false,
      edit: true
    }
  };

  tableNew_config = {
    columns: [
      {
        key: 'quoteId',
        heading: 'Application #'
      },
      {
        key: 'idNumber',
        heading: 'Id Number'
      },
      {
        key: 'firstName',
        heading: 'Name'
      },
      {
        key: 'lastName',
        heading: 'Surname'
      },
      {
        key: 'status',
        heading: 'Status',
        status: true
      },
      {
        key: 'userName',
        heading: 'User'
      }
    ],
    primary_key_set: ['quoteId'],
    table_data_changer: new BehaviorSubject<any[]>([]),
    ediTable: {
      add: false,
      edit: true
    }
  };

  ComplianceListCount: number;

  tableCompliance_config = {
    columns: [
      {
        key: 'quoteId',
        heading: 'Application Number'
      },
      {
        key: 'firstName',
        heading: 'Full Names'
      },
      {
        key: 'lastName',
        heading: 'Surname'
      },
      {
        key: 'idNumber',
        heading: 'ID Number'
      },
      {
        key: 'status',
        heading: 'Status'
      },
      {
        key: 'userName',
        heading: 'User'
      }
    ],
    primary_key_set: ['quoteId'],
    table_data_changer:  new BehaviorSubject<any[]>([]),
    ediTable: {
      add: false,
      edit: false
    }
  };

  private destroy$ = new Subject<void>();

  ActiveClientListCount: number;
  NewClientListCount: number;
  private selectedBranchSubject = new BehaviorSubject<string>('');

  constructor(
    public global_utility: AppUtilityService,
    public landingService: LandingService,
    private userService: UserService,
    private lmsDialogHandler: LMSDialogHandlerService,
    private quoteStoreService: QuoteStoreService,
    private cdr: ChangeDetectorRef,
    private storageService: StorageService
  ) {
    const subs = this.landingService.lmsUser$.pipe(
      filter(profiledata => profiledata != null), // filters out null and undefined
      tap((profile) => {
        console.log('Profile for landing Page:', profile)
      })).subscribe({
        next: (profile) => {
          if (profile){
            this.LandingProfile = profile;
            console.log('✅ VALID Landing Profile!');
            this.quoteStoreService.setQuoteWorkbook(null);

            if(!this.userService.isFingerAuth && this.withFingerCheck) {
              this.lmsDialogHandler.openSigninDialog().onClose.subscribe((dialogResult: boolean) => {
                this.userService.setFingerValid(dialogResult);
              });
            }

            this.tableCompliance_config.ediTable.edit = this.LandingProfile?.jobTitle?.jobTitle!.toLowerCase() === 'branch manager' ? true : false;

            if(this._userbranchName != profile.branchName) {
              this._userbranchName = profile.branchName;
              this._userbranchSerial = profile.delfinBranchSerial;
              this._userDisplayName = profile.nameSurname;
              if(this._userbranchName && this._userbranchSerial) {
                this.changeBranch({ branchName: profile.branchName, branchSerial: profile.delfinBranchSerial});
              }
            }
          }
        },
        complete: () => {
          this.global_utility.stopLoading();
        },
        error: (err) => {
           console.error('❗Profile BAD Load: ', err);
          this.global_utility.stopLoading();
        },
      });
    this.subscriptions.push(subs);
  }


  ngOnInit(): void {
    this.showFetch = false;

    // Check user preference for landing layout (overrides environment setting)
    const savedPref = this.storageService.getLandingLayoutPreference();
    this.useNewLandingLayout = savedPref !== null ? savedPref : this.useNewLandingLayout;


    // Initialize filtered observables
    this.initializeFilteredObservables();

    if(this.enableClientList){
      this.activeClientList$.pipe(takeUntil(this.destroy$)).subscribe(([activeClients, newClients, complianceClients]) => {
        this.tableActive_config.table_data_changer.next(activeClients);
        this.tableNew_config.table_data_changer.next(newClients);
        this.tableCompliance_config.table_data_changer.next(complianceClients);
      });

      this.subscriptions.push(this.landingService.clientList$.subscribe(arg => this.ActiveClientListCount = arg.length));
      this.subscriptions.push(this.landingService.newClientList$.subscribe(arg => this.NewClientListCount = arg.length));
      this.subscriptions.push(this.landingService.complianceList$.subscribe(arg => this.ComplianceListCount = arg.length));
    }
  }

  private initializeFilteredObservables(): void {
    // Create loading observable that switches based on selected category
    this.isLoading$ = this.selectedCategory$.pipe(
      switchMap(category => {
        if (category === 'active') {
          return this.landingService.clientListLoading$;
        } else if (category === 'new') {
          return this.landingService.newListLoading$;
        } else if (category === 'compliance') {
          return this.landingService.clientListLoading$;
        }
        return of(false);
      })
    );

    // Create a single filtered observable that switches between data sources based on selected category
    this.filteredQuotes$ = combineLatest([
      this.selectedCategory$,
      this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        map(term => term?.toLowerCase() || '')
      ),
      this.statusFilterControl.valueChanges.pipe(startWith('all')),
      this.sortControl.valueChanges.pipe(startWith('dateDesc'))
    ]).pipe(
      switchMap(([category, searchTerm, statusFilter, sortBy]) => {
        // Select the appropriate data source based on selected category
        let dataSource$: Observable<any[]>;

        if (category === 'active') {
          dataSource$ = this.tableActive_config.table_data_changer.asObservable().pipe(startWith([]));
        } else if (category === 'new') {
          dataSource$ = this.tableNew_config.table_data_changer.asObservable().pipe(startWith([]));
        } else if (category === 'compliance') {
          dataSource$ = this.tableCompliance_config.table_data_changer.asObservable().pipe(startWith([]));
        } else {
          // No category selected, return empty array
          return of([]);
        }

        return dataSource$.pipe(
          map(clients => {
            // Ensure we always have an array
            if (!clients || !Array.isArray(clients)) {
              console.warn('dataSource$ emitted non-array value:', clients);
              return [];
            }

            let filtered = clients || [];

            // If list is empty or has single quote, reset filters to defaults for better UX
            if (filtered.length <= 1) {
              // Reset to defaults if not already
              if (this.searchControl.value && this.searchControl.value.length > 0) {
                this.searchControl.setValue('', { emitEvent: false });
              }
              if (this.statusFilterControl.value !== 'all') {
                this.statusFilterControl.setValue('all', { emitEvent: false });
              }
              if (this.sortControl.value !== 'dateDesc') {
                this.sortControl.setValue('dateDesc', { emitEvent: false });
              }
              // Return early if no items - no need to filter/sort
              return filtered;
            }

            // Apply search filter
            if (searchTerm) {
              filtered = filtered.filter(client =>
                client.firstName?.toLowerCase().includes(searchTerm) ||
                client.lastName?.toLowerCase().includes(searchTerm) ||
                client.idNumber?.toLowerCase().includes(searchTerm) ||
                client.cellNumber?.includes(searchTerm) ||
                client.quoteId?.toString().includes(searchTerm)
              );
            }

            // Apply status filter
            if (statusFilter && statusFilter !== 'all') {
              filtered = filtered.filter(client =>
                client.status?.toLowerCase().includes(statusFilter.toLowerCase())
              );
            }

            // Apply sorting
            return this.sortClients(filtered, sortBy);
          })
        );
      })
    );
  }

  selectCategory(category: 'active' | 'new' | 'compliance'): void {
    this.selectedCategory = category;
    this.selectedCategory$.next(category);
    // Clear filters when switching categories
    this.clearFilters();
  }

  get LandingBranchName() {
    return this._userbranchName || this.global_utility.getGlobalData('branch_Name') || '';
  }

  get LandingBranchSerial() {
    return this._userbranchSerial || this.global_utility.getGlobalData('branch_serial') || '';
  }

  get LandingSelectedBranch() {
    return this._selectedBranch || this.global_utility.getGlobalData('branch_serial') || {};
  }

  changeBranch(branch?: any): void {
    if (branch && branch.branchSerial !== 'undefined') {
      this._selectedBranch = branch;
      if(this.onbranchChanged) {
        this.onbranchChanged.emit(this._selectedBranch);
      }
      this.global_utility.stopLoading();
    }
  }

  viewAllLoans(): void {
    const branchSerial = this.landingService.selectedBranch ?? this._selectedBranch;
    if (!branchSerial) return;

    this.global_utility.startLoading('Fetching branch data...', 1);
    this.onSelectedBranch(branchSerial, true);

    // Delay UI presentation slightly to avoid spinner flash
      this.showFetch = true;
      if(this.onViewAllClients){
        this.onViewAllClients.emit();
      }
      this.global_utility.stopLoading(); // Stop once panel fires
  }


  onSelectedBranch(event: any, forceLoad = false): void {
    const branch = event?.branchSerial ? event : event?.target;

    const isValidBranch = branch?.branchSerial !== 'undefined' && branch?.branchName !== 'undefined';
    if (!branch || !isValidBranch) {
      if (!forceLoad) return;
      //this.userService.setNoMenus(true);
      this.global_utility.stopLoading();
      return;
    }

    this.showFetch = false;
    const resolvedBranchSerial = branch?.branchSerial;
    this.selectedBranchSubject.next(resolvedBranchSerial);
    // if(this.onbranchChanged) {
    //   this.onbranchChanged.emit(branch);
    // }
  }

  readonly activeClientList$ = this.selectedBranchSubject.pipe(
    tap(branchSerial => {
      if(!branchSerial){
        branchSerial = this.global_utility.getGlobalData('branch_serial');
      }

      if(branchSerial){
        this.landingService.findNewByBranch(this.global_utility.getGlobalData('user_LoginName'), branchSerial);
        this.landingService.findQuotesByBranch(this.global_utility.getGlobalData('user_LoginName'), branchSerial);
         this.landingService.findComplianceByBranch(this.global_utility.getGlobalData('user_LoginName'), branchSerial)
      }
    }),
    switchMap(() =>
      combineLatest([
        this.landingService.clientList$,
        this.landingService.newClientList$,
        this.landingService.complianceList$
      ])
    )
  );

  viewClient(quote: any): void {
    console.log('Route from landing component through service');
    this.landingService.viewClient(quote);
  }
  viewNewClient(quote: any): void {
    console.log('Route from landing component through service');
    this.landingService.viewNewClient(quote);
  }

  ComplianceUpdate(quote: any): void {
    console.log('Route from landing component through service');
    this.onComplianceUpdate.emit(quote);
    //this.landingService.compliance(quote);
  }

  onActiveClientsListValue = (table_data: any) => {
    this.tableActive_config.table_data_changer.next(table_data);
    //this.ActiveClientListCount = table_data?.length ?? 0;
  };

  onNewClientsListValue = (table_data: any) => {
     this.tableNew_config.table_data_changer.next(table_data);
    //this.NewClientListCount = table_data.length ?? 0;
  };

  onComplianceListValue = (table_data: any) => {
    this.tableCompliance_config.table_data_changer.next(table_data);
    //this.ComplianceListCount = table_data?.length;
  };

  setpanelOpenState(value: number) {
    //this.bmlandingComponent?.panelOpenState!! == value;
    this.PanelOpenState = value;
    this.togglePanelOpenstate.emit(value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.subscriptions.forEach(x => {
      x.unsubscribe();
    });
  }

  // Filter and sort methods
  private sortClients(clients: any[], sortBy: string | null): any[] {
    if (!sortBy || !clients || clients.length === 0) return clients;

    if(Array.isArray(clients) == false) return clients;
    const sorted = [...clients];

    switch(sortBy) {
      case 'dateDesc':
        return sorted.sort((a, b) =>
          (b.submittedDate || b.createdDate || 0) - (a.submittedDate || a.createdDate || 0)
        );
      case 'dateAsc':
        return sorted.sort((a, b) =>
          (a.submittedDate || a.createdDate || 0) - (b.submittedDate || b.createdDate || 0)
        );
      case 'applicationAsc':
        return sorted.sort((a, b) => (a.quoteId || 0) - (b.quoteId || 0));
      case 'applicationDesc':
        return sorted.sort((a, b) => (b.quoteId || 0) - (a.quoteId || 0));
      case 'nameAsc':
        return sorted.sort((a, b) =>
          (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName)
        );
      case 'nameDesc':
        return sorted.sort((a, b) =>
          (b.firstName + ' ' + b.lastName).localeCompare(a.firstName + ' ' + a.lastName)
        );
      default:
        return sorted;
    }
  }

  clearFilters(): void {
    this.searchControl.setValue('', { emitEvent: true });
    this.statusFilterControl.setValue('all', { emitEvent: true });
    this.sortControl.setValue('dateDesc', { emitEvent: true });    this.cdr.markForCheck();  }

  get hasActiveFilters(): boolean {
    return (this.searchControl.value && this.searchControl.value.length > 0) ||
           this.statusFilterControl.value !== 'all';
  }

  // Computed properties for metrics
  get activeClientsMetrics() {
    const clients = this.tableActive_config.table_data_changer.value || [];
    const total = clients.length;
    const urgentCount = clients.filter(c => this.isUrgent(c)).length;
    const avgDaysActive = this.calculateAverageDaysActive(clients);
    const nupayCount = clients.filter(c =>
      c.status?.toLowerCase().includes('nupay')
    ).length;
    const completedCount = clients.filter(c =>
      c.status?.toLowerCase().includes('disbursed')
    ).length;

    return {
      total,
      avgLoanAmount: this.calculateAverage(clients, 'capital'),
      totalValue: this.calculateSum(clients, 'capital'),
      urgentCount,
      avgDaysActive,
      nupayCount,
      completionRate: total > 0 ? (completedCount / total) * 100 : 0
    };
  }

  getEmptyStateTitle(): string {
    switch(this.selectedCategory) {
      case 'active': return 'No Active Clients';
      case 'new': return 'No New or Pending Clients';
      case 'compliance': return 'No Compliance Clients';
      default: return 'No Clients';
    }
  }

  getEmptyStateMessage(): string {
    switch(this.selectedCategory) {
      case 'active': return 'There are no active loan applications at this time.';
      case 'new': return 'There are no new or pending applications at this time.';
      case 'compliance': return 'There are no compliance clients at this time.';
      default: return 'Select a category to view clients.';
    }
  }

  get newClientsMetrics() {
    const clients = this.tableNew_config.table_data_changer.value || [];
    const total = clients.length;
      const newCount = Array.isArray(clients) == false ? 0 : clients.filter(c => c.status === 'New').length;
      const pendingCount = Array.isArray(clients) == false ? 0 :clients.filter(c => c.status === 'Pending').length;
      const submittedCount = Array.isArray(clients) == false ? 0 :clients.filter(c =>
        c.status?.toLowerCase().includes('submitted') ||
        c.status?.toLowerCase().includes('approved')
      ).length;

    return {
      total,
      avgLoanAmount: this.calculateAverage(clients, 'capital'),
      totalValue: this.calculateSum(clients, 'capital'),
      newCount,
      pendingCount,
      submissionRate: total > 0 ? (submittedCount / total) * 100 : 0
    };
  }

  get complianceMetrics() {
    const clients = this.tableCompliance_config.table_data_changer.value || [];
    const requiresAttention = clients.filter(c =>
      c.status?.toLowerCase().includes('pending') ||
      c.status?.toLowerCase().includes('review') ||
      !c.status
    ).length;
    const resolved = clients.filter(c =>
      c.status?.toLowerCase().includes('resolved') ||
      c.status?.toLowerCase().includes('completed') ||
      c.status?.toLowerCase().includes('approved')
    ).length;

    return {
      total: clients.length,
      requiresAttention,
      resolved
    };
  }

  // Utility methods
  private calculateAverage(data: any[], key: string): number {
    if (!data || data.length === 0) return 0;
    if(Array.isArray(data) == false) return 0;

    const sum = data.reduce((acc, item) => acc + (Number(item[key]) || 0), 0);
    return sum / data.length;
  }

  private calculateSum(data: any[], key: string): number {
     if (!data || data.length === 0) return 0;
    if(Array.isArray(data) == false) return 0;

    return data.reduce((acc, item) => acc + (Number(item[key]) || 0), 0);
  }

  private calculateAverageDaysActive(data: any[]): number {
     if (!data || data.length === 0) return 0;
    if(Array.isArray(data) == false) return 0;

    const now = Date.now();
    const daysArray = data
      .filter(item => item.createdDate || item.submittedDate)
      .map(item => {
        const date = new Date(item.createdDate || item.submittedDate).getTime();
        return Math.floor((now - date) / (1000 * 60 * 60 * 24));
      });

    if (daysArray.length === 0) return 0;
    const sum = daysArray.reduce((acc, days) => acc + days, 0);
    return sum / daysArray.length;
  }

  private isUrgent(quote: any): boolean {
    // Consider urgent if pending for more than 3 days
    if (!quote.submittedDate) return false;
    const daysDiff = Math.floor((Date.now() - new Date(quote.submittedDate).getTime()) / (1000 * 60 * 60 * 24));
    return quote.status === 'Pending' && daysDiff > 3;
  }

  trackByQuoteId(index: number, item: any): number {
    return item?.quoteId || index;
  }

  getStatusClass(status: string): string {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('active')) return 'status-active';
    if (statusLower.includes('new')) return 'status-new';
    if (statusLower.includes('pending')) return 'status-pending';
    if (statusLower.includes('submitted')) return 'status-submitted';
    if (statusLower.includes('nupay')) return 'status-nupay';
    return '';
  }

  getBadgeClass(status: string): string {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('active')) return 'badge-active';
    if (statusLower.includes('new')) return 'badge-new';
    if (statusLower.includes('pending')) return 'badge-pending';
    if (statusLower.includes('submitted')) return 'badge-submitted';
    if (statusLower.includes('nupay')) return 'badge-nupay';
    return 'badge-default';
  }

  selectQuote(quote: any): void {
    const status = quote?.status?.toLowerCase() || '';

    if (status.includes('active')) {
      this.viewClient(quote);
    } else if (status.includes('new') || status.includes('pending')) {
      this.viewNewClient(quote);
    } else if (status.includes('submitted')) {
      // Navigate to loan agreement or appropriate route
      this.viewClient(quote);
    } else {
      this.viewClient(quote);
    }
  }

  toggleViewMode(mode?: 'cards' | 'table'): void {
    if (mode) {
      this.viewMode = mode;
    } else {
      this.viewMode = this.viewMode === 'cards' ? 'table' : 'cards';
    }
  }

}
